import {defineStore} from 'pinia'
import {getUserInfo} from "@/api/user";
import { logoutSSR } from '~/api/auth-ssr';
import type {GetUserInfoResponse} from "~/utils/types";

// BI P2 follow-up:set_user_id 的實際 dataLayer push 動作,updateAnalyticsUserId
// 與 flushAnalyticsUserId 共用,避免兩處各寫一次一樣的 window.dataLayer.push。
function pushSetUserId(id: string): void {
    if (typeof window === 'undefined') return
    const w = window as unknown as { dataLayer?: Record<string, unknown>[] }
    w.dataLayer = w.dataLayer || []
    w.dataLayer.push({ event: 'set_user_id', user_id: id })
}

interface useUserStoreState {
    account: string,
    token: string,
    name: string,
    phone: string,
    email: string,
    accountType: number,
    analyticsUserId: string
}


export const useUserStore = defineStore('user', {
    state: (): useUserStoreState => {
        return {
            account: '',
            token: '',
            name: '',
            phone: '',
            email: '',
            accountType: -1,
            analyticsUserId: ''
        }
    },
    getters: {
        getToken: (state) => {
            return state.token
        },
        getAccount: (state) => {
            return state.account
        },
        getName: (state) => {
            return state.name
        },
        getPhone: (state) => {
            return state.phone
        },
        getEmail: (state) => {
            return state.email
        },
        getAccountType: (state) => {
            return state.accountType
        },
        getAnalyticsUserId: (state) => {
            return state.analyticsUserId
        },

    },
    actions: {
        async updateUserInfo(): Promise<void> {
            try {
                const res: GetUserInfoResponse = await getUserInfo();
                this.updateAccount(res.account);
                this.updateName(res.name);
                this.updatePhone(res.phone);
                this.updateEmail(res.email);
                this.updateAccountType(res.account_type);
                // BI P2:Others.BehaviorAnalytics.Enabled=false 時後端不輸出這個 key,
                // 不呼叫 updateAnalyticsUserId——GA4 端維持只有 _ga 匿名 id,是預期的降級狀態。
                if (res.analytics_user_id) {
                    this.updateAnalyticsUserId(res.analytics_user_id);
                }
            } catch (error) {
                console.log(error);
            }
        },
        async logout(): Promise<void> {

            try {
                await logoutSSR(this.account)
            } catch (error) {
                console.log(error)
            }
            this.updateAccount('');
            this.updateToken('');
            this.updateName('');
            this.updatePhone('');
            this.updateEmail('');
            this.updateAccountType(-1);
            this.updateAnalyticsUserId('');
        },
        updateAccount(email: string): void {
            this.account = email
        },
        updateToken(tokenString: string): void {
            this.token = tokenString
           /* const {setToken} = useAuth()
            setToken(tokenString)*/
            // 收藏統一(RR P1):登入把訪客暫存的收藏合併進 API;登出回到本地訪客收藏。
            // 涵蓋所有登入入口(帳密 / Google / LINE),因為它們都經由這個 setter 設 token。
            if (import.meta.client) {
                const favoritesStore = useFavoritesStore()
                if (tokenString) {
                    favoritesStore.flushOnLogin()
                } else {
                    favoritesStore.resetToLocal()
                }
            }
        },
        updateName(nameString: string): void {
            this.name = nameString
        },
        updatePhone(phoneString: string): void {
            this.phone = phoneString
        },
        updateEmail(emailString: string): void {
            this.email = emailString
        },
        updateAccountType(type: number): void {
            this.accountType = type
        },
        // BI P2:GTM 端用這個 dataLayer 事件設定 GA4 的 user_id 欄位,
        // 沿用既有「事件驅動 GTM 變數」慣例,不直接呼叫 gtag('config',...)
        // 覆蓋 GTM 既有的 GA4 tag 設定。id 為空(登出/未取得)時只清狀態,不送事件。
        //
        // consentModeEnabled 開啟時,set_user_id 會識別使用者,不能跟其他
        // cookieless ping(ADR 0027)一樣不分同意與否都送:尚未同意或已拒絕就
        // 只存 id、不推事件,等 grantConsent() 或回訪已同意重播時由
        // flushAnalyticsUserId() 補推。旗標關閉時維持原行為,立刻推。
        updateAnalyticsUserId(id: string): void {
            this.analyticsUserId = id
            if (!id) return
            const { getStoredConsentChoice, shouldPushAnalyticsUserId } = useConsent()
            const consentModeEnabled = useRuntimeConfig().public.consentModeEnabled
            if (shouldPushAnalyticsUserId(consentModeEnabled, getStoredConsentChoice())) {
                pushSetUserId(id)
            }
        },
        // BI P2 follow-up:consent 從尚未回應/拒絕變成同意時(grantConsent 或
        // plugins/consent.client.ts 的回訪已同意重播)呼叫,補推被 updateAnalyticsUserId
        // 擋下的 set_user_id。沒有暫存 id 就什麼都不做。
        flushAnalyticsUserId(): void {
            if (!this.analyticsUserId) return
            pushSetUserId(this.analyticsUserId)
        }
    },
    persist: {
        storage: persistedState.cookiesWithOptions({}),
    },
})

